import { deepLinkingConfig } from "../navigation/config/DeepLinkingConfig";
import { validateRouteAccess, getRouteConfig } from "./RouteProtectionConfig";
import { getLoginData } from "./AppStoreData";

/**
 * Navigation Helper - Ensures proper URL-based navigation with route protection
 *
 * This helper converts screen names to their proper deep link URLs
 * and navigates using paths instead of screen names, ensuring the
 * address bar shows the correct path.
 *
 * Now includes centralized route protection validation.
 */

// Type definitions for deep linking config
interface ScreenConfig {
  path?: string;
  screens?: Record<string, string | ScreenConfig>;
  parse?: Record<string, (value: string) => any>;
  stringify?: Record<string, (value: any) => string>;
  initialRouteName?: string;
}

interface DeepLinkConfig {
  screens?: Record<string, string | ScreenConfig>;
}

// Flatten the deep linking config to create a screen -> URL map
function buildScreenUrlMap(
  config: DeepLinkConfig,
  parentPath: string = ""
): Record<string, string> {
  const map: Record<string, string> = {};

  if (!config || typeof config !== "object") return map;

  if (config.screens) {
    Object.entries(config.screens).forEach(([screenName, screenConfig]) => {
      if (typeof screenConfig === "string") {
        // Direct URL mapping: ScreenName: "/path"
        // Skip empty strings (used to satisfy TabStack initialRouteName without creating URL mapping)
        if (screenConfig !== "") {
          const fullPath = screenConfig.startsWith("/")
            ? screenConfig
            : `${parentPath}/${screenConfig}`;
          map[screenName] = fullPath;
        }
      } else if (screenConfig && typeof screenConfig === "object") {
        // Check if this navigator has a base path
        const navigatorPath = (screenConfig as ScreenConfig).path || "";
        const newParentPath = navigatorPath
          ? navigatorPath.startsWith("/")
            ? navigatorPath
            : `${parentPath}/${navigatorPath}`
          : parentPath;

        // Check if it has a path property for this screen (for screens with parse config)
        const configPath = (screenConfig as ScreenConfig).path;
        const configScreens = (screenConfig as ScreenConfig).screens;

        if (
          configPath !== undefined &&
          typeof configPath === "string" &&
          !configScreens
        ) {
          // This is a screen with path config, not a navigator
          if (configPath === "") {
            // Empty path means use the parent path (from TabStack)
            map[screenName] = parentPath || "/";
          } else {
            const fullPath = configPath.startsWith("/")
              ? configPath
              : `${parentPath}/${configPath}`;
            map[screenName] = fullPath;
          }
        }

        // Also check for nested navigator/screens
        if (configScreens) {
          const nestedMap = buildScreenUrlMap(
            { screens: configScreens },
            newParentPath
          );
          Object.assign(map, nestedMap);
        }
      }
    });
  }

  return map;
}

// Build the map once at module load
const webScreenStack = (deepLinkingConfig as any).screens?.WebScreenStack;
const screenToUrlMap = buildScreenUrlMap(webScreenStack || {});

// Debug logging - check if DrawerRoute is being processed
console.log("🔍 [NavigationHelper] Raw WebScreenStack:", webScreenStack);
console.log(
  "🗺️ [NavigationHelper] Loaded with",
  Object.keys(screenToUrlMap).length,
  "screens"
);
console.log(
  "🔍 [NavigationHelper] SettingWebScreen maps to:",
  screenToUrlMap["SettingWebScreen"]
);
console.log("🔍 [NavigationHelper] Help maps to:", screenToUrlMap["Help"]);
console.log(
  "🔍 [NavigationHelper] HelpScreen maps to:",
  screenToUrlMap["HelpScreen"]
);
console.log("📋 [NavigationHelper] Full URL Map:", screenToUrlMap);

// Check DrawerRoute specifically
const drawerConfig = webScreenStack?.screens?.DrawerRoute as
  | ScreenConfig
  | undefined;
if (drawerConfig) {
  console.log("🚪 [NavigationHelper] DrawerRoute found:", drawerConfig);
  console.log(
    "🚪 [NavigationHelper] SettingTabStack config:",
    drawerConfig.screens?.SettingTabStack
  );
}

/**
 * Navigate to a screen using its proper deep link URL with route protection
 *
 * @param navigation - The navigation object from useNavigation()
 * @param screenName - The screen name from AppScreens
 * @param params - Optional navigation parameters
 * @param skipAuthCheck - Skip authentication check (use with caution)
 *
 * @example
 * navigateToScreen(navigation, 'CreatePublicEventScreen', { eventType: 'Public' })
 * // Navigates to: /events/create/public?eventType=Public
 */
export async function navigateToScreen(
  navigation: any,
  screenName: string,
  params?: Record<string, any>,
  skipAuthCheck: boolean = false
) {
  const url = screenToUrlMap[screenName];

  if (!url) {
    console.warn(`[NavigationHelper] No URL found for screen: ${screenName}`);
    console.warn(
      "[NavigationHelper] Available screens:",
      Object.keys(screenToUrlMap)
    );
    // Fallback to regular navigation
    navigation.navigate(screenName, params);
    return;
  }

  // Route protection check
  if (!skipAuthCheck) {
    try {
      const loginData = await getLoginData();
      const isLoggedIn = loginData !== null;
      const validation = validateRouteAccess(url, isLoggedIn);

      if (!validation.allowed) {
        console.warn(
          `[NavigationHelper] Access denied to ${screenName} (${url})`
        );
        console.warn(`[NavigationHelper] Reason: ${validation.reason}`);

        // Redirect to setup screen with original path
        if (validation.redirectTo) {
          const redirectUrl = validation.redirectTo;
          console.log(`[NavigationHelper] Redirecting to: ${redirectUrl}`);

          if (typeof window !== "undefined") {
            window.history.pushState(null, "", redirectUrl);
            navigation.navigate("SetupScreen", { path: url });
          }
        }
        return;
      }

      const routeConfig = getRouteConfig(url);
      console.log(
        `[NavigationHelper] Access granted - ${screenName} (Category: ${routeConfig?.category})`
      );
    } catch (error) {
      console.error(
        "[NavigationHelper] Error checking route protection:",
        error
      );
      // Continue with navigation on error (fail open for now)
    }
  }

  // Add default tab parameters for specific screens if no tab is provided
  const defaultTabParams: Record<string, Record<string, string>> = {
    SettingWebScreen: { tab: "account" },
    Help: { tab: "gettingStarted" },
  };

  console.log(`[NavigationHelper] Attempting to navigate to: ${screenName}`);
  console.log(`[NavigationHelper] URL from map: ${url}`);
  console.log(`[NavigationHelper] Params provided:`, params);

  // Merge default params with provided params (provided params take precedence)
  let finalParams = params || {};
  if (defaultTabParams[screenName] && !finalParams.tab) {
    console.log(
      `[NavigationHelper] Adding default tab param for ${screenName}`
    );
    finalParams = { ...defaultTabParams[screenName], ...finalParams };
  }

  console.log(`[NavigationHelper] Final params:`, finalParams);

  // Clone params for query string processing (keep originals for navigation)
  const queryParams = { ...finalParams };
  let fullUrl = url;

  // Handle path parameters (e.g. /streaming/:username)
  if (fullUrl.includes(":")) {
    const parts = fullUrl.split("/");
    const newParts = parts.map((part) => {
      if (part.startsWith(":")) {
        const paramName = part.substring(1);
        // Check if we have this param (and it's not undefined/null)
        if (
          queryParams[paramName] !== undefined &&
          queryParams[paramName] !== null
        ) {
          const value = queryParams[paramName];
          delete queryParams[paramName]; // Remove from query string params
          return encodeURIComponent(String(value));
        }
      }
      return part;
    });
    fullUrl = newParts.join("/");
  }

  // Build query string from REMAINING params
  if (Object.keys(queryParams).length > 0) {
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join("&");

    // Check if url already has query params
    const separator = fullUrl.includes("?") ? "&" : "?";
    fullUrl = `${fullUrl}${separator}${queryString}`;
  }

  console.log(`[NavigationHelper] Navigating: ${screenName} -> ${fullUrl}`);

  // On web, navigate using the path directly to avoid React Navigation's URL generation
  if (typeof window !== "undefined") {
    // Push the path directly to history
    window.history.pushState(null, "", fullUrl);

    // Then navigate using screen name so React Navigation updates its state
    navigation.navigate(screenName, finalParams);

    // Ensure URL stays correct after React Navigation processes
    setTimeout(() => {
      if (
        window.location.pathname !== url &&
        window.location.pathname !== fullUrl
      ) {
        window.history.replaceState(null, "", fullUrl);
        console.log(`[NavigationHelper] URL corrected to: ${fullUrl}`);
      }
    }, 100);
  } else {
    navigation.navigate(screenName, finalParams);
  }
}

/**
 * Get the URL for a screen without navigating
 */
export function getScreenUrl(
  screenName: string,
  params?: Record<string, any>
): string | null {
  const url = screenToUrlMap[screenName];

  if (!url) return null;

  // Clone params for query string processing
  const queryParams = params ? { ...params } : {};
  let fullUrl = url;

  // Handle path parameters
  if (fullUrl.includes(":")) {
    const parts = fullUrl.split("/");
    const newParts = parts.map((part) => {
      if (part.startsWith(":")) {
        const paramName = part.substring(1);
        if (
          queryParams[paramName] !== undefined &&
          queryParams[paramName] !== null
        ) {
          const value = queryParams[paramName];
          delete queryParams[paramName];
          return encodeURIComponent(String(value));
        }
      }
      return part;
    });
    fullUrl = newParts.join("/");
  }

  if (Object.keys(queryParams).length > 0) {
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join("&");

    const separator = fullUrl.includes("?") ? "&" : "?";
    return `${fullUrl}${separator}${queryString}`;
  }

  return fullUrl;
}

// Export the map for debugging
export { screenToUrlMap };
